import { Button, DownloadSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import ChevronThinRightIcon from "@/icons/ChevronThinRightIcon";
import ExternalLinkIcon from "@/icons/ExternalLinkIcon";
import uiTypes from "@/view/constants/uiTypes";
import { TGeniusPanelOptionType } from "@/view/GeniusContent/GeniusActivityPanel/GeniusActivityPanel.constant";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import { buttonCss, iconCss, rootCss } from "./GeniusActivityPanelOption.style";
import type { GenerationGeniusContent } from "@/view/GeniusContent/GeniusContent.interface";

interface GeniusActivityPanelOptionProps {
  type?: TGeniusPanelOptionType | undefined;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  content: GenerationGeniusContent;
}

const ButtonWithTrackEvent = withTrackEvent(Button);

const GeniusActivityPanelOption = ({
  type = "internal-panel-link",
  onClick,
  children,
  content,
}: GeniusActivityPanelOptionProps) => {
  const { uiType } = useUserContentUIContext();
  const liteUserPremiumContent = uiType === uiTypes.LITE_USER_PREMIUM_CONTENT;
  const isDownloadType = type === "download";
  const isExternalLink = type === "external-link";
  const isExternalResource = isDownloadType || isExternalLink;

  const getIcon = () => {
    switch (type) {
      case "download":
        return <DownloadSVG />;
      case "external-link":
        return <ExternalLinkIcon />;
      default:
        return <ChevronThinRightIcon />;
    }
  };

  return (
    <li css={rootCss}>
      <ButtonWithTrackEvent
        flavor={Button.flavors.incognito}
        disabled={liteUserPremiumContent}
        __cssFor={{
          root: buttonCss(liteUserPremiumContent, isExternalResource),
        }}
        onClick={onClick}
        dataProps={{
          "data-qa-selector": `generation-genius-activity-option-${children}`,
        }}
        ariaProps={{
          "aria-label": `${isDownloadType ? "Download " : ""}${children}`,
          "aria-disabled": liteUserPremiumContent,
        }}
        objectType={objectTypes.content}
        actionName={
          isDownloadType ? actionNames.download : actionNames.navigate
        }
        streamProperties={{
          description: `Click Generations Genius activity ${children}`,
          content_id: content.id,
          content_type: "GenerationGeniusVideo",
        }}
      >
        {children}
        <span css={iconCss(isExternalResource)} data-testid="icon">
          {getIcon()}
        </span>
      </ButtonWithTrackEvent>
    </li>
  );
};

export default GeniusActivityPanelOption;
